import { buildReactorName } from "@/src/utils/reactor-name.utils";
export { buildReactorName };
import { useAuth } from "@/src/contexts/auth.context";
import { Post, ReactionType } from "@/src/models/post.model";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import {
  useGetUserPostReaction,
  useUpdatePostScoreMutation,
} from "@/src/services/api/posts/posts.hooks";
import { hot } from "@/src/utils/feed-score";
import { useUserReactionQueryEnabled } from "./use-feed-reactions";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { countersEnabled } from "@/src/services/api/posts/engagement-functions";
import Toast from "react-native-toast-message";
import { react, useReactionView } from "./reaction-sync";

export const useReactToPost = (post: Post) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const userId = user?.itemId ?? "";

  // Inside a feed the page's reactions are fetched together and this query
  // reads the result out of the cache; on its own it resolves normally.
  const reactionQueryEnabled = useUserReactionQueryEnabled();
  const { data: userReaction } = useGetUserPostReaction(post.ItemId, userId, {
    enabled: reactionQueryEnabled,
  });
  const queryClient = useQueryClient();
  const { mutate: updateScore } = useUpdatePostScoreMutation();
  const { mutate: sendNotification } = useSendNotification();
  const serverType = userReaction?.type ?? null;
  const view = useReactionView(post.ItemId, userId, serverType, post.reactionCount ?? {});

  const handleReact = (requested: ReactionType | null) => {
    if (!userId) return;
    // One reaction per user: the same one again removes it, another replaces it.
    const next = requested && requested === view.type ? null : requested;
    react({
      queryClient,
      postId: post.ItemId,
      userId,
      serverType,
      serverCounts: post.reactionCount ?? {},
      next,
      reactorName: buildReactorName(user?.firstName, user?.lastName),
      reactorProfileImageUrl: user?.profileImageUrl ?? undefined,
      onFailed: () => Toast.show({ type: "error", text1: t("LABEL_REACTION_FAILED") }),
      onCommitted: (final, start, counts) => {
        if (final === start) return;
        const total = Object.values(counts).reduce<number>((a, b) => a + (b ?? 0), 0);
        if (!countersEnabled) {
          updateScore({ id: post.ItemId, score: hot(total, post.commentCount ?? 0, new Date(post.CreatedDate)) });
        }
        if (!final) return;
        sendNotification({
          userIds: [post.authorSnapshot.userId],
          denormalizedPayload: JSON.stringify({
            type: "post_reaction",
            message: t("LABEL_NOTIFY_POST_REACTION", { firstName: user?.firstName, lastName: user?.lastName }),
            actorAvatar: user?.profileImageUrl,
            targetEntityId: post.ItemId,
          }),
          configurationName: "post-liked",
          responseKey: "post-action",
          responseValue: final as string,
        });
      },
    });
  };

  return {
    userReaction: { type: view.type, id: userReaction?.id ?? null },
    reactionCount: view.counts as Post["reactionCount"],
    reactionPending: view.pending,
    handleReact,
  };
};
